import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ConfirmDialog from '../components/ConfirmDialog';
import ExportPdfButton from '../components/ExportPdfButton';
import PeriodSelector, { PERIOD_OPTIONS } from '../components/PeriodSelector';
import TimesheetSummaryView from '../components/TimesheetSummaryView';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay, differenceInCalendarWeeks } from 'date-fns';
import {
  CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  Trash2,
  X,
  Pencil,
  ClipboardList
} from 'lucide-react';

const TimesheetPage = () => {
  const { api, user } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, entryId: null });
  const [deleting, setDeleting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  // 'week' keeps the editable weekly grid; month/quarter/year are consolidated summaries.
  const [period, setPeriod] = useState('week');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    hours: '',
    description: '',
    task_id: ''
  });

  const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });
  const daysOfWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    fetchData();
  }, [selectedWeek]);

  useEffect(() => {
    if (period === 'week') return undefined;
    let cancelled = false;
    setSummaryLoading(true);
    api.get('/timesheets/summary', { params: { period, offset: periodOffset } })
      .then(res => { if (!cancelled) setSummary(res.data); })
      .catch(() => { if (!cancelled) { setSummary(null); toast.error('Failed to load timesheet summary'); } })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [period, periodOffset]);

  const handlePeriodChange = (nextPeriod) => {
    setPeriod(nextPeriod);
    setPeriodOffset(0);
    if (nextPeriod === 'week') {
      setSelectedWeek(new Date());
    } else {
      setShowForm(false);
      setSelectedDate(null);
      resetForm();
    }
  };

  // The API counts offsets back from the current period; future weeks clamp to the current one.
  const exportOffset = period === 'week'
    ? Math.max(0, differenceInCalendarWeeks(new Date(), selectedWeek, { weekStartsOn: 1 }))
    : periodOffset;

  // Server-generated PDF for the period on screen, used by Export PDF.
  const timesheetReport = {
    path: '/timesheets/summary/pdf',
    params: { period, offset: exportOffset },
    filename: `syncly-timesheet-${period}-${period === 'week' ? format(weekStart, 'yyyy-MM-dd') : (summary?.start_date || 'report')}.pdf`,
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const startStr = format(weekStart, 'yyyy-MM-dd');
      const endStr = format(weekEnd, 'yyyy-MM-dd');

      const [timesheetsRes, tasksRes] = await Promise.all([
        api.get(`/timesheets/my?start_date=${startStr}&end_date=${endStr}`),
        api.get('/tasks')
      ]);

      setTimesheets(timesheetsRes.data);
      // Handle paginated or flat tasks response
      const allTasks = tasksRes.data.tasks || tasksRes.data;
      const taskList = Array.isArray(allTasks) ? allTasks : [];
      const myTasks = taskList.filter(t =>
        Array.isArray(t.assigned_to) && t.assigned_to.includes(user?.id) && t.status !== 'completed'
      );
      setTasks(myTasks);
    } catch (error) {
      toast.error('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      hours: '',
      description: '',
      task_id: ''
    });
    setEditingEntry(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate date is not in the future
    const entryDate = new Date(formData.date);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (entryDate > now) {
      toast.error('Cannot log timesheet for a future date');
      return;
    }

    const hoursVal = parseFloat(formData.hours);
    if (!formData.hours || isNaN(hoursVal) || hoursVal < 0.1 || hoursVal > 24) {
      toast.error('Hours must be between 0.1 and 24');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: formData.date,
        hours: parseFloat(formData.hours),
        description: formData.description.trim(),
        task_id: formData.task_id || null
      };

      if (editingEntry) {
        await api.put(`/timesheets/${editingEntry.id}`, payload);
        toast.success('Timesheet entry updated');
      } else {
        await api.post('/timesheets', payload);
        toast.success('Timesheet entry saved');
      }

      setShowForm(false);
      setSelectedDate(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (entryId) => {
    setDeleteConfirm({ open: true, entryId });
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await api.delete(`/timesheets/${deleteConfirm.entryId}`);
      toast.success('Entry deleted');
      setDeleteConfirm({ open: false, entryId: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  const openFormForDate = (date) => {
    setEditingEntry(null);
    setSelectedDate(date);
    setSelectedWeek(date);
    setFormData({
      date: format(date, 'yyyy-MM-dd'),
      hours: '',
      description: '',
      task_id: ''
    });
    setShowForm(true);
  };

  const openFormForEdit = (entry) => {
    setEditingEntry(entry);
    setSelectedDate(new Date(entry.date));
    setFormData({
      date: entry.date,
      hours: String(entry.hours),
      description: entry.description,
      task_id: entry.task_id || ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedDate(null);
    resetForm();
  };

  const getEntriesForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return timesheets.filter(t => t.date === dateStr);
  };

  const getTotalHoursForDate = (date) => {
    return getEntriesForDate(date).reduce((sum, t) => sum + t.hours, 0);
  };

  const getTotalWeekHours = () => {
    return timesheets.reduce((sum, t) => sum + t.hours, 0);
  };

  if (loading && period === 'week') {
    return (
      <div className="flex items-center justify-center h-64" data-testid="timesheet-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight uppercase">TIMESHEET</h1>
          <p className="text-muted-foreground text-sm">
            Track your daily work hours
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportPdfButton api={api} {...timesheetReport} testId="timesheet-export-pdf-btn" />
          <Button
            onClick={() => {
              if (period !== 'week') handlePeriodChange('week');
              openFormForDate(new Date());
            }}
            data-testid="add-timesheet-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            LOG HOURS
          </Button>
        </div>
      </div>

      {period !== 'week' && (
        <>
          <PeriodSelector
            period={period}
            onPeriodChange={handlePeriodChange}
            offset={periodOffset}
            onOffsetChange={setPeriodOffset}
            caption="CONSOLIDATED TIMESHEET"
            label={summary?.label || '…'}
            testIdPrefix="timesheet-period"
          />
          <TimesheetSummaryView summary={summary} loading={summaryLoading} />
        </>
      )}

      {period === 'week' && (
      <>

      {/* Week Navigation */}
      <Card className="border-border">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-4 gap-1 bg-muted p-1" role="tablist" aria-label="Timesheet period">
            {PERIOD_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={value === 'week'}
                onClick={() => handlePeriodChange(value)}
                className={`py-1.5 text-[10px] sm:text-xs font-medium tracking-wider transition-colors ${
                  value === 'week' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`timesheet-period-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setSelectedWeek(subWeeks(selectedWeek, 1)); setSelectedDate(null); setShowForm(false); resetForm(); }}
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center">
              <p className="text-sm text-muted-foreground uppercase tracking-wider">
                WEEK OF
              </p>
              <p className="text-lg font-bold">
                {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setSelectedWeek(addWeeks(selectedWeek, 1)); setSelectedDate(null); setShowForm(false); resetForm(); }}
              data-testid="next-week-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Week Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
        {daysOfWeek.map(day => {
          const entries = getEntriesForDate(day);
          const totalHours = getTotalHoursForDate(day);
          const isToday = isSameDay(day, new Date());
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isFuture = day.getTime() > new Date().getTime();

          return (
            <Card
              key={day.toISOString()}
              className={`border-border transition-colors ${isSelected ? 'border-primary' : ''} ${isFuture ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50 cursor-pointer'}`}
              onClick={!isFuture ? () => openFormForDate(day) : undefined}
              data-testid={`day-card-${format(day, 'yyyy-MM-dd')}`}
            >
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                  <span>{format(day, 'EEE')}</span>
                  {isToday ? (
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                      {format(day, 'd')}
                    </span>
                  ) : (
                    <span>{format(day, 'd')}</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="text-2xl font-bold">{totalHours.toFixed(1)}h</div>
                {entries.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {entries.slice(0, 2).map(entry => (
                      <div
                        key={entry.id}
                        className="text-[10px] text-muted-foreground truncate"
                        title={entry.description}
                      >
                        {entry.description}
                      </div>
                    ))}
                    {entries.length > 2 && (
                      <div className="text-[10px] text-primary">
                        +{entries.length - 2} more
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add/Edit Entry Form */}
      {showForm && (
        <Card className="border-primary">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base sm:text-lg uppercase">
              {editingEntry
                ? `EDIT ENTRY — ${format(new Date(formData.date), 'MMM d, yyyy')}`
                : `LOG HOURS — ${format(new Date(formData.date), 'MMM d, yyyy')}`
              }
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={closeForm}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>DATE</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="timesheet-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                        <span className="truncate">{format(new Date(formData.date), 'MMM d, yyyy')}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={new Date(formData.date)}
                        onSelect={(date) => {
                          if (date) {
                            setFormData(prev => ({
                              ...prev,
                              date: format(date, 'yyyy-MM-dd')
                            }));
                            setSelectedDate(date);
                            setSelectedWeek(date);
                          }
                        }}
                        disabled={{ after: new Date() }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hours">HOURS *</Label>
                  <Input
                    id="hours"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="24"
                    value={formData.hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                    required
                    placeholder="1.0"
                    data-testid="timesheet-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>RELATED TASK (OPTIONAL)</Label>
                  <Select
                    value={formData.task_id || "none"}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, task_id: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger data-testid="timesheet-task-select">
                      <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No task</SelectItem>
                      {tasks.map(task => (
                        <SelectItem key={task.id} value={task.id}>{task.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">DESCRIPTION *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  required
                  rows={2}
                  placeholder="What did you work on?"
                  data-testid="timesheet-description-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="secondary" onClick={closeForm}>
                  CANCEL
                </Button>
                <Button type="submit" disabled={saving} data-testid="timesheet-submit-btn">
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {editingEntry ? 'UPDATE ENTRY' : 'SAVE ENTRY'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Week Summary */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            WEEK TOTAL: {getTotalWeekHours().toFixed(1)} HOURS
          </CardTitle>
        </CardHeader>
        <CardContent>
          {timesheets.length > 0 ? (
            <div className="space-y-2">
              {timesheets.map(entry => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-3 border border-border hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => openFormForEdit(entry)}
                  data-testid={`timesheet-entry-${entry.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-muted-foreground font-mono">
                        {entry.date}
                      </span>
                      <span className="font-medium">{entry.hours}h</span>
                      {entry.task_title && (
                        <span className="text-xs text-primary">[{entry.task_title}]</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{entry.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFormForEdit(entry);
                      }}
                      data-testid={`edit-entry-${entry.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(entry.id);
                      }}
                      data-testid={`delete-entry-${entry.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground font-medium mb-1">
                No timesheet entries this week
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Log your first hours to get started.
              </p>
              <Button onClick={() => openFormForDate(new Date())} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                LOG YOUR FIRST ENTRY
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirm({ open: false, entryId: null });
        }}
        title="Delete timesheet entry?"
        description="This will permanently remove this timesheet entry. This action cannot be undone."
        confirmLabel="DELETE"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        loading={deleting}
      />
    </div>
  );
};

export default TimesheetPage;
